<template>
  <section class="py-14 lg:py-20 bg-[#111]" aria-labelledby="bundle-heading">
    <div class="max-w-screen-xl mx-auto px-6">

      <SectionHeader
        eyebrow="Complete oplossing"
        title="Alles voor jouw energiethuis"
        heading-id="bundle-heading"
        dark
      />

      <div v-if="loading" class="flex justify-center py-16">
        <div class="w-10 h-10 border-4 border-[#333] border-t-[#FFD500] rounded-full animate-spin"></div>
      </div>

      <div v-else class="grid lg:grid-cols-[1fr_360px] gap-4 items-stretch min-w-0">

        <!-- ── Links: featured hero product ── -->
        <div class="bg-[#1a1a1a] rounded-2xl flex flex-col overflow-hidden border border-[#2a2a2a] min-w-0">

          <!-- Kop -->
          <div class="px-5 md:px-7 pt-6 md:pt-7 pb-4">
            <p class="text-[1.1rem] sm:text-[1.2rem] lg:text-[1.6rem] font-black text-white leading-snug break-words">
              {{ hero.headline }}
            </p>
          </div>

          <!-- Product afbeelding -->
          <div class="flex-1 flex items-center justify-center px-8 py-4 min-h-[200px]">
            <img
              v-if="hero.image"
              :src="hero.image"
              :alt="hero.name"
              class="max-h-[220px] max-w-full w-auto object-contain drop-shadow-md"
              loading="lazy"
              decoding="async"
            />
            <div v-else class="w-40 h-40 rounded-2xl bg-white/5 flex items-center justify-center">
              <i class="bi bi-box-seam text-[3rem] text-white/20"></i>
            </div>
          </div>

          <!-- Gele onderkaart -->
          <div class="bg-[#FFD500] mx-3 md:mx-4 mb-3 md:mb-4 rounded-xl p-4 md:p-5 flex items-end justify-between gap-3 flex-wrap">
            <div class="min-w-0 flex-1">
              <p class="text-[0.7rem] font-bold uppercase tracking-widest text-[#111] mb-1 opacity-80">
                {{ hero.category }}
              </p>
              <h3 class="text-[#111] font-black text-[1.1rem] leading-snug break-words">{{ hero.name }}</h3>
              <p class="text-[#111]/80 text-[0.8rem] mt-0.5 break-words">{{ hero.subtitle }}</p>
              <!-- Levertijd hero -->
              <div v-if="hero.id && deliveryTimes[hero.id]" class="inline-flex items-center gap-1.5 mt-2 text-[0.72rem] font-semibold bg-black/10 text-[#111] px-2 py-0.5 rounded-full">
                <i :class="['bi text-[0.78rem]', deliveryTimes[hero.id].icon]"></i>
                {{ deliveryTimes[hero.id].label }}
              </div>
            </div>
            <router-link
              :to="hero.link"
              class="inline-flex items-center gap-2 bg-[#111] hover:bg-[#222] text-white font-bold text-[0.875rem] px-4 py-2.5 rounded-lg no-underline transition-colors whitespace-nowrap shrink-0"
            >
              Bestel direct
              <i class="bi bi-arrow-right"></i>
            </router-link>
          </div>
        </div>

        <!-- ── Rechts: 3 gestapelde productkaartjes ── -->
        <div class="flex flex-col gap-3 min-w-0">
          <router-link
            v-for="p in sideProducts"
            :key="p.slug"
            :to="`/product/${p.slug}`"
            class="group bg-[#1a1a1a] rounded-xl border border-[#2a2a2a] hover:border-[#FFD500] hover:shadow-sm transition-all no-underline flex items-center gap-4 p-4"
          >
            <!-- Thumbnail -->
            <div class="w-[60px] h-[60px] rounded-lg bg-[#222] flex items-center justify-center shrink-0 overflow-hidden">
              <img
                v-if="p.thumbnail"
                :src="p.thumbnail"
                :alt="p.name"
                class="w-full h-full object-contain p-1"
                loading="lazy"
              />
              <i v-else class="bi bi-box-seam text-[1.5rem] text-[#555]"></i>
            </div>

            <!-- Info -->
            <div class="flex-1 min-w-0">
              <!-- Categorie-label -->
              <span class="text-[0.65rem] font-bold uppercase tracking-wider text-[#FFD500]">
                {{ p.categoryName }}
              </span>
              <h4 class="text-[0.875rem] font-bold text-white leading-snug mt-0.5 truncate group-hover:text-[#FFD500] transition-colors">
                {{ p.name }}
              </h4>
              <!-- Specs: SKU + eerste 3 attributen -->
              <p class="text-[0.72rem] text-[#9a9a9a] mt-0.5 truncate">
                <span v-if="p.sku">{{ p.sku }}</span>
                <template v-for="(attr, i) in (p.attributes || []).slice(0, 3)" :key="i">
                  <span> • </span>
                  <span>{{ attr.options?.[0] }}</span>
                </template>
              </p>
              <!-- Levertijd -->
              <div v-if="deliveryTimes[p.id]" class="inline-flex items-center gap-1 mt-1 text-[0.68rem] font-semibold" :style="`color:${deliveryTimes[p.id].color}`">
                <i :class="['bi text-[0.72rem]', deliveryTimes[p.id].icon]"></i>
                {{ deliveryTimes[p.id].label }}
              </div>
            </div>

            <i class="bi bi-chevron-right text-[#444] text-[0.8rem] shrink-0 group-hover:text-[#FFD500] transition-colors"></i>
          </router-link>

          <!-- Meer bekijken -->
          <router-link
            to="/producten"
            class="flex items-center justify-center gap-2 text-[0.85rem] font-semibold text-[#FFD500] hover:text-white transition-colors no-underline py-2"
          >
            Bekijk alle producten
            <i class="bi bi-arrow-right text-[0.8rem]"></i>
          </router-link>
        </div>

      </div>
    </div>
  </section>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import SectionHeader from './SectionHeader.vue'
import wooApi from '@/api/woocommerce.js'

const loading      = ref(true)
const deliveryTimes = ref({})   // Map: productId → { label, sub, color, icon }
const hero        = ref({
  name:     'Laad- en energiepakket',
  headline: 'Speel flexibel in op elke energiebehoefte.',
  subtitle: 'Modulair, schaalbaar en toekomstbestendig',
  category: 'Aanbevolen',
  image:    null,
  link:     '/producten',
  id:       null,
})
const sideProducts = ref([])

async function fetchDeliveryTime(productId) {
  try {
    const res = await fetch(`/_delivery-time/${productId}`)
    if (res.ok) deliveryTimes.value[productId] = await res.json()
  } catch {}
}

onMounted(async () => {
  try {
    // Haal de eerste 4 uitgelichte producten op
    const featured = await wooApi.getProducts({ featured: true, per_page: 4, status: 'publish' })

    if (featured.length > 0) {
      const h = featured[0]
      const autoHeadline = `Met de ${h.name.split('|')[0].trim()} speel je flexibel in op elke energiebehoefte.`
      const autoSubtitle = h.short_description?.replace(/<[^>]*>/g, '').trim().slice(0, 70) || 'Modulair, schaalbaar en toekomstbestendig'
      hero.value = {
        name:     h.name,
        headline: h.featured_headline || autoHeadline,
        subtitle: h.featured_subtitle || autoSubtitle,
        category: h.categories?.[0]?.name || 'Aanbevolen',
        image:    h.thumbnail,
        link:     `/product/${h.slug}`,
        id:       h.id,
      }
      fetchDeliveryTime(h.id)
    }

    // De volgende 3 als zij-producten
    sideProducts.value = featured.slice(1, 4).map(p => ({
      ...p,
      categoryName: p.categories?.[0]?.name || 'Product',
    }))

    // Vul aan met populaire producten als er minder dan 3 uitgelicht zijn
    if (sideProducts.value.length < 3) {
      const popular = await wooApi.getProducts({
        orderby:  'popularity',
        per_page: 3 - sideProducts.value.length + 1,
        status:   'publish',
      })
      const existing = new Set([featured[0]?.id, ...sideProducts.value.map(p => p.id)])
      const extra = popular
        .filter(p => !existing.has(p.id))
        .slice(0, 3 - sideProducts.value.length)
        .map(p => ({ ...p, categoryName: p.categories?.[0]?.name || 'Product' }))
      sideProducts.value.push(...extra)
    }

    // Levertijden ophalen voor zij-producten
    sideProducts.value.forEach(p => fetchDeliveryTime(p.id))

  } catch (err) {
    console.error('[HomeFeaturedBundle]', err)
  } finally {
    loading.value = false
  }
})
</script>
